import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, XCircle } from "lucide-react";
import { toast } from "sonner";

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("checking");
  const navigate = useNavigate();
  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    authApi.verify(token).then(() => setStatus("ok")).catch(() => setStatus("invalid"));
  }, [token]);
  return (
    <div className="min-h-screen grid place-items-center bg-[#090A0F] text-slate-100 px-6">
      <div className="aq-card p-8 max-w-md w-full text-center">
        {status === "checking" && <div className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400">Verifying…</div>}
        {status === "ok" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 grid place-items-center"><Check className="w-7 h-7 text-emerald-400" /></div>
            <h1 className="mt-4 font-display text-2xl font-bold">Email verified</h1>
            <p className="mt-2 text-sm text-slate-400">Your TradingoX account is confirmed.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Go to terminal</Button>
          </>
        )}
        {status === "invalid" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-rose-500/20 grid place-items-center"><XCircle className="w-7 h-7 text-rose-400" /></div>
            <h1 className="mt-4 font-display text-2xl font-bold">Link invalid or expired</h1>
            <Button onClick={() => navigate("/")} className="mt-6 w-full h-11 rounded-full bg-white/10 hover:bg-white/20 text-white">Back to home</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const submit = async () => {
    if (!pw || pw.length < 8) return toast.error("Password must be 8+ chars");
    try {
      setBusy(true);
      await authApi.reset(token, pw);
      toast.success("Password reset — sign in with your new password");
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-[#090A0F] text-slate-100 px-6">
      <div className="aq-card p-8 max-w-md w-full">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">Reset password</div>
        <h1 className="mt-2 font-display text-2xl font-bold">Choose a new password</h1>
        <Input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
          placeholder="New password (8+ chars)"
          className="mt-4 bg-[#0D0E14] border-white/10 h-11" />
        <Button data-testid="reset-password-submit" onClick={submit} disabled={busy}
          className="mt-3 w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
          {busy ? "…" : "Save new password"}
        </Button>
      </div>
    </div>
  );
}
